import { normalizeWorkbookMetricHeadings } from "./workbookMetricHeadings";
export type WorkbookCell = string | number | boolean | null;
export type WorkbookRowKind = "header" | "detail" | "subtotal" | "total";
export type WorkbookWarningRule = { column: number; threshold: number };
export type WorkbookProgressExpectation = { expected: number; expectedLabel: string };
export type WorkbookProgressComparison = WorkbookProgressExpectation & {
  column: number;
  rowExpectations?: (WorkbookProgressExpectation | null)[];
  includeTotal?: boolean;
};

export type WorkbookSheet = {
  name: string;
  rows: WorkbookCell[][];
  title?: string;
  subtitle?: string;
  integers?: number[];
  decimals?: number[];
  percentages?: number[];
  percentagePoints?: number[];
  dates?: number[];
  dateTimes?: number[];
  durations?: number[];
  widths?: number[];
  rowKinds?: WorkbookRowKind[];
  brandColumn?: number;
  colorScaleColumns?: number[];
  colorScaleColors?: [string, string, string];
  colorScaleThresholds?: [number, number, number];
  warningRules?: WorkbookWarningRule[];
  progressComparisons?: WorkbookProgressComparison[];
  wrapColumns?: number[];
  theme?: "standard" | "report" | "info";
  totalStyle?: "yellow" | "bold";
  autoFilter?: boolean;
  tabColor?: string;
  freezeColumns?: number;
  landscape?: boolean;
};

export type WorkbookProperties = {
  title?: string;
  subject?: string;
  creator?: string;
  reportKind?: "store-performance";
};

type StyleDefinition = {
  numFmtId: number;
  fontId: number;
  fillId: number;
  borderId: number;
  horizontal: "left" | "center" | "right";
  wrap?: boolean;
};

type WorkbookStylePlan = {
  comparisonStyleIds: Map<string, number>;
  comparisonFormats: { numFmtId: number; formatCode: string; styles: StyleDefinition[] }[];
};

const STYLE = {
  bodyText: 0,
  bodyInteger: 1,
  bodyDecimal: 2,
  bodyPercentage: 3,
  bodyPercentagePoint: 4,
  bodyDate: 5,
  standardHeader: 6,
  reportHeader: 7,
  title: 8,
  subtitle: 9,
  brand: 10,
  subtotalText: 11,
  subtotalInteger: 12,
  subtotalDecimal: 13,
  subtotalPercentage: 14,
  subtotalPercentagePoint: 15,
  subtotalDate: 16,
  totalBlueText: 17,
  totalBlueInteger: 18,
  totalBlueDecimal: 19,
  totalBluePercentage: 20,
  totalBluePercentagePoint: 21,
  totalBlueDate: 22,
  totalYellowText: 23,
  totalYellowInteger: 24,
  totalYellowDecimal: 25,
  totalYellowPercentage: 26,
  totalYellowPercentagePoint: 27,
  totalYellowDate: 28,
  infoLabel: 29,
  infoValue: 30,
  wrappedText: 31,
  bodyDateTime: 32,
  bodyDuration: 33,
  subtotalDateTime: 34,
  subtotalDuration: 35,
  totalBlueDateTime: 36,
  totalBlueDuration: 37,
  totalYellowDateTime: 38,
  totalYellowDuration: 39,
} as const;

const STYLE_DEFINITIONS: StyleDefinition[] = [
  { numFmtId: 0, fontId: 0, fillId: 0, borderId: 1, horizontal: "left" },
  { numFmtId: 164, fontId: 0, fillId: 0, borderId: 1, horizontal: "right" },
  { numFmtId: 165, fontId: 0, fillId: 0, borderId: 1, horizontal: "right" },
  { numFmtId: 166, fontId: 0, fillId: 0, borderId: 1, horizontal: "right" },
  { numFmtId: 167, fontId: 0, fillId: 0, borderId: 1, horizontal: "right" },
  { numFmtId: 168, fontId: 0, fillId: 0, borderId: 1, horizontal: "center" },
  { numFmtId: 0, fontId: 1, fillId: 2, borderId: 2, horizontal: "center", wrap: true },
  { numFmtId: 0, fontId: 2, fillId: 3, borderId: 3, horizontal: "center", wrap: true },
  { numFmtId: 0, fontId: 3, fillId: 0, borderId: 0, horizontal: "left" },
  { numFmtId: 0, fontId: 4, fillId: 0, borderId: 0, horizontal: "left", wrap: true },
  { numFmtId: 0, fontId: 5, fillId: 4, borderId: 1, horizontal: "left" },
  { numFmtId: 0, fontId: 5, fillId: 5, borderId: 4, horizontal: "left" },
  { numFmtId: 164, fontId: 5, fillId: 5, borderId: 4, horizontal: "right" },
  { numFmtId: 165, fontId: 5, fillId: 5, borderId: 4, horizontal: "right" },
  { numFmtId: 166, fontId: 5, fillId: 5, borderId: 4, horizontal: "right" },
  { numFmtId: 167, fontId: 5, fillId: 5, borderId: 4, horizontal: "right" },
  { numFmtId: 168, fontId: 5, fillId: 5, borderId: 4, horizontal: "center" },
  { numFmtId: 0, fontId: 5, fillId: 6, borderId: 5, horizontal: "left" },
  { numFmtId: 164, fontId: 5, fillId: 6, borderId: 5, horizontal: "right" },
  { numFmtId: 165, fontId: 5, fillId: 6, borderId: 5, horizontal: "right" },
  { numFmtId: 166, fontId: 5, fillId: 6, borderId: 5, horizontal: "right" },
  { numFmtId: 167, fontId: 5, fillId: 6, borderId: 5, horizontal: "right" },
  { numFmtId: 168, fontId: 5, fillId: 6, borderId: 5, horizontal: "center" },
  { numFmtId: 0, fontId: 5, fillId: 7, borderId: 5, horizontal: "left" },
  { numFmtId: 164, fontId: 5, fillId: 7, borderId: 5, horizontal: "right" },
  { numFmtId: 165, fontId: 5, fillId: 7, borderId: 5, horizontal: "right" },
  { numFmtId: 166, fontId: 5, fillId: 7, borderId: 5, horizontal: "right" },
  { numFmtId: 167, fontId: 5, fillId: 7, borderId: 5, horizontal: "right" },
  { numFmtId: 168, fontId: 5, fillId: 7, borderId: 5, horizontal: "center" },
  { numFmtId: 0, fontId: 5, fillId: 8, borderId: 1, horizontal: "left" },
  { numFmtId: 0, fontId: 0, fillId: 8, borderId: 1, horizontal: "left", wrap: true },
  { numFmtId: 0, fontId: 0, fillId: 0, borderId: 1, horizontal: "left", wrap: true },
  { numFmtId: 169, fontId: 0, fillId: 0, borderId: 1, horizontal: "center" },
  { numFmtId: 170, fontId: 0, fillId: 0, borderId: 1, horizontal: "right" },
  { numFmtId: 169, fontId: 5, fillId: 5, borderId: 4, horizontal: "center" },
  { numFmtId: 170, fontId: 5, fillId: 5, borderId: 4, horizontal: "right" },
  { numFmtId: 169, fontId: 5, fillId: 6, borderId: 5, horizontal: "center" },
  { numFmtId: 170, fontId: 5, fillId: 6, borderId: 5, horizontal: "right" },
  { numFmtId: 169, fontId: 5, fillId: 7, borderId: 5, horizontal: "center" },
  { numFmtId: 170, fontId: 5, fillId: 7, borderId: 5, horizontal: "right" },
];

function cleanXml(value: string) {
  return value.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, "");
}

function escapeXml(value: string) {
  return cleanXml(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function columnName(value: number) {
  let name = "";
  for (let index = value; index > 0; index = Math.floor((index - 1) / 26)) {
    name = String.fromCharCode(65 + ((index - 1) % 26)) + name;
  }
  return name;
}

function normalizeColor(value: string | undefined, fallback: string) {
  const color = (value ?? fallback).replace(/^#/, "").toUpperCase();
  return color.length === 6 ? `FF${color}` : color.length === 8 ? color : fallback;
}

function normalizeSheets(sheets: WorkbookSheet[]) {
  const used = new Set<string>();
  return sheets.map((sheet, index) => {
    const base = cleanXml(sheet.name).replace(/[\[\]*\\/:?]/g, " ").trim().slice(0, 31) || `Rapor ${index + 1}`;
    let name = base;
    let suffix = 2;
    while (used.has(name.toLocaleLowerCase("tr-TR"))) {
      const addition = ` ${suffix++}`;
      name = `${base.slice(0, 31 - addition.length)}${addition}`;
    }
    used.add(name.toLocaleLowerCase("tr-TR"));
    return { ...sheet, name };
  });
}

function parseDateSerial(value: WorkbookCell) {
  if (typeof value !== "string") return null;
  const text = value.trim();
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const display = text.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  const year = iso ? Number(iso[1]) : display ? Number(display[3]) : 0;
  const month = iso ? Number(iso[2]) : display ? Number(display[2]) : 0;
  const day = iso ? Number(iso[3]) : display ? Number(display[1]) : 0;
  if (!year || month < 1 || month > 12 || day < 1 || day > 31) return null;
  const utc = Date.UTC(year, month - 1, day);
  const check = new Date(utc);
  if (check.getUTCFullYear() !== year || check.getUTCMonth() !== month - 1 || check.getUTCDate() !== day) return null;
  return (utc - Date.UTC(1899, 11, 30)) / 86_400_000;
}

function parseDateTimeSerial(value: WorkbookCell) {
  if (typeof value !== "string" || !value.trim()) return null;
  const text = value.trim();
  const local = text.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?$/);
  if (local) {
    const serial = Date.UTC(Number(local[1]), Number(local[2]) - 1, Number(local[3]), Number(local[4]), Number(local[5]), Number(local[6] ?? 0));
    return (serial - Date.UTC(1899, 11, 30)) / 86_400_000;
  }
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Tashkent",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(parsed).reduce<Record<string, number>>((result, part) => {
    if (part.type !== "literal") result[part.type] = Number(part.value);
    return result;
  }, {});
  const serial = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
  return (serial - Date.UTC(1899, 11, 30)) / 86_400_000;
}

function temporalStyle(sheet: WorkbookSheet, column: number, kind: WorkbookRowKind) {
  const isDateTime = sheet.dateTimes?.includes(column);
  const isDuration = sheet.durations?.includes(column);
  if (!isDateTime && !isDuration) return null;
  if (kind === "subtotal") return isDateTime ? STYLE.subtotalDateTime : STYLE.subtotalDuration;
  if (kind === "total") {
    if (sheet.totalStyle === "yellow") return isDateTime ? STYLE.totalYellowDateTime : STYLE.totalYellowDuration;
    return isDateTime ? STYLE.totalBlueDateTime : STYLE.totalBlueDuration;
  }
  return isDateTime ? STYLE.bodyDateTime : STYLE.bodyDuration;
}

function numberStyle(sheet: WorkbookSheet, column: number, offset: number) {
  if (sheet.dates?.includes(column)) return STYLE.bodyDate + offset;
  if (sheet.percentages?.includes(column)) return STYLE.bodyPercentage + offset;
  if (sheet.percentagePoints?.includes(column)) return STYLE.bodyPercentagePoint + offset;
  if (sheet.decimals?.includes(column)) return STYLE.bodyDecimal + offset;
  if (sheet.integers?.includes(column)) return STYLE.bodyInteger + offset;
  return STYLE.bodyText + offset;
}

function progressExpectation(rule: WorkbookProgressComparison, rowIndex: number): WorkbookProgressExpectation | null {
  if (rule.rowExpectations && rowIndex < rule.rowExpectations.length) return rule.rowExpectations[rowIndex];
  return { expected: rule.expected, expectedLabel: rule.expectedLabel };
}

function progressStyleKey(label: string, kind: WorkbookRowKind, totalStyle: WorkbookSheet["totalStyle"]) {
  if (kind === "total") return `${label}|${totalStyle === "yellow" ? "total-yellow" : "total-blue"}`;
  return `${label}|detail`;
}

function cellStyle(sheet: WorkbookSheet, rowIndex: number, columnIndex: number, stylePlan: WorkbookStylePlan) {
  const column = columnIndex + 1;
  const kind = sheet.rowKinds?.[rowIndex] ?? (rowIndex === 0 ? "header" : "detail");
  if (kind === "header") return sheet.theme === "report" ? STYLE.reportHeader : STYLE.standardHeader;
  if (sheet.theme === "info") return columnIndex === 0 ? STYLE.infoLabel : STYLE.infoValue;
  const temporal = temporalStyle(sheet, column, kind);
  if (temporal != null) return temporal;
  const progressComparison = sheet.progressComparisons?.find((item) => item.column === column);
  if ((kind === "detail" || (kind === "total" && progressComparison?.includeTotal)) && progressComparison) {
    const expectation = progressExpectation(progressComparison, rowIndex);
    if (expectation) {
      return stylePlan.comparisonStyleIds.get(progressStyleKey(expectation.expectedLabel, kind, sheet.totalStyle)) ?? STYLE.bodyPercentagePoint;
    }
  }
  if (kind === "subtotal") return numberStyle(sheet, column, STYLE.subtotalText - STYLE.bodyText);
  if (kind === "total") {
    const base = sheet.totalStyle === "yellow" ? STYLE.totalYellowText : STYLE.totalBlueText;
    return numberStyle(sheet, column, base - STYLE.bodyText);
  }
  if (sheet.wrapColumns?.includes(column)) return STYLE.wrappedText;
  if (sheet.brandColumn === column) return STYLE.brand;
  return numberStyle(sheet, column, 0);
}

function numericCellValue(sheet: WorkbookSheet, column: number, value: number) {
  if (sheet.percentagePoints?.includes(column)) return value / 100;
  if (sheet.durations?.includes(column)) return value / 86_400;
  return value;
}

export function validateWorkbookSheets(sheets: WorkbookSheet[]) {
  if (!sheets.length) throw new Error("Excel çıktısı en az bir sayfa içermelidir.");
  sheets.forEach((sheet) => {
    if (!sheet.rows.length || !sheet.rows[0]?.length) throw new Error(`${sheet.name}: başlık satırı eksik.`);
    const columnCount = sheet.rows[0].length;
    if (sheet.rowKinds && sheet.rowKinds.length !== sheet.rows.length) throw new Error(`${sheet.name}: satır türleri veri satırlarıyla eşleşmiyor.`);
    sheet.rows.forEach((row, rowIndex) => {
      if (row.length > columnCount) throw new Error(`${sheet.name}: ${rowIndex + 1}. satır başlık sayısından fazla sütun içeriyor.`);
    });
    if (sheet.widths && sheet.widths.length !== columnCount) throw new Error(`${sheet.name}: sütun genişlikleri başlıklarla eşleşmiyor.`);

    const groups = [sheet.integers, sheet.decimals, sheet.percentages, sheet.percentagePoints, sheet.dates, sheet.dateTimes, sheet.durations].filter(Boolean) as number[][];
    const seen = new Set<number>();
    groups.flat().forEach((column) => {
      if (!Number.isInteger(column) || column < 1 || column > columnCount) throw new Error(`${sheet.name}: ${column}. sütun için geçersiz Excel biçimi tanımlandı.`);
      if (seen.has(column)) throw new Error(`${sheet.name}: ${column}. sütunda birden fazla veri biçimi tanımlandı.`);
      seen.add(column);
    });
    [sheet.brandColumn, ...(sheet.colorScaleColumns ?? []), ...(sheet.wrapColumns ?? []), ...(sheet.warningRules ?? []).map((item) => item.column), ...(sheet.progressComparisons ?? []).map((item) => item.column)]
      .filter((column): column is number => column != null)
      .forEach((column) => {
        if (!Number.isInteger(column) || column < 1 || column > columnCount) throw new Error(`${sheet.name}: ${column}. sütuna ait Excel ayarı başlıkların dışında kalıyor.`);
      });

    if (sheet.theme === "info") return;
    sheet.rows.slice(1).forEach((row, rowIndex) => row.forEach((value, columnIndex) => {
      const column = columnIndex + 1;
      const kind = rowKind(sheet, rowIndex + 1);
      if (typeof value === "number" && Number.isFinite(value) && !seen.has(column)) {
        throw new Error(`${sheet.name}: ${rowIndex + 2}. satır ${column}. sütundaki sayının Excel biçimi tanımlanmamış.`);
      }
      if (kind === "detail" && value != null && value !== "" && sheet.dates?.includes(column) && parseDateSerial(value) == null) {
        throw new Error(`${sheet.name}: ${rowIndex + 2}. satır ${column}. sütunda geçersiz tarih var.`);
      }
      if (kind === "detail" && value != null && value !== "" && sheet.dateTimes?.includes(column) && parseDateTimeSerial(value) == null) {
        throw new Error(`${sheet.name}: ${rowIndex + 2}. satır ${column}. sütunda geçersiz tarih-saat var.`);
      }
    }));
  });
}

function rowKind(sheet: WorkbookSheet, rowIndex: number): WorkbookRowKind {
  return sheet.rowKinds?.[rowIndex] ?? (rowIndex === 0 ? "header" : "detail");
}

function detailRanges(sheet: WorkbookSheet, column: number, firstDataRow: number) {
  const ranges: string[] = [];
  let start: number | null = null;
  for (let index = 1; index < sheet.rows.length; index += 1) {
    const detail = rowKind(sheet, index) === "detail";
    const excelRow = firstDataRow + index;
    if (detail && start == null) start = excelRow;
    if ((!detail || index === sheet.rows.length - 1) && start != null) {
      const end = detail && index === sheet.rows.length - 1 ? excelRow : excelRow - 1;
      ranges.push(`${columnName(column)}${start}:${columnName(column)}${end}`);
      start = null;
    }
  }
  return ranges.join(" ");
}

function sheetLayout(sheet: WorkbookSheet) {
  const hasIntro = Boolean(sheet.title || sheet.subtitle);
  const firstDataRow = hasIntro ? 4 : 1;
  const headerRow = firstDataRow;
  const lastRow = Math.max(firstDataRow, firstDataRow + sheet.rows.length - 1);
  const columnCount = Math.max(1, ...sheet.rows.map((row) => row.length));
  return { firstDataRow, headerRow, lastRow, columnCount, lastColumn: columnName(columnCount) };
}

function sheetXml(sheet: WorkbookSheet, stylePlan: WorkbookStylePlan) {
  const { firstDataRow, headerRow, lastRow, columnCount, lastColumn } = sheetLayout(sheet);
  const introRows = firstDataRow === 4
    ? `<row r="1" ht="26" customHeight="1"><c r="A1" s="${STYLE.title}" t="inlineStr"><is><t xml:space="preserve">${escapeXml(sheet.title ?? sheet.name)}</t></is></c></row><row r="2" ht="30" customHeight="1"><c r="A2" s="${STYLE.subtitle}" t="inlineStr"><is><t xml:space="preserve">${escapeXml(sheet.subtitle ?? "")}</t></is></c></row><row r="3" ht="8" customHeight="1"/>`
    : "";
  const body = sheet.rows.map((row, rowIndex) => {
    const excelRow = firstDataRow + rowIndex;
    const kind = rowKind(sheet, rowIndex);
    const height = kind === "header" ? 32 : kind === "detail" ? 20 : 22;
    const cells = Array.from({ length: columnCount }, (_, columnIndex) => {
      const value = row[columnIndex] ?? null;
      const ref = `${columnName(columnIndex + 1)}${excelRow}`;
      const style = cellStyle(sheet, rowIndex, columnIndex, stylePlan);
      const dateTimeSerial = sheet.dateTimes?.includes(columnIndex + 1) ? parseDateTimeSerial(value) : null;
      const dateSerial = sheet.dates?.includes(columnIndex + 1) ? parseDateSerial(value) : null;
      if (dateTimeSerial != null) return `<c r="${ref}" s="${style}"><v>${dateTimeSerial}</v></c>`;
      if (dateSerial != null) return `<c r="${ref}" s="${style}"><v>${dateSerial}</v></c>`;
      if (typeof value === "number" && Number.isFinite(value)) return `<c r="${ref}" s="${style}"><v>${numericCellValue(sheet, columnIndex + 1, value)}</v></c>`;
      if (typeof value === "boolean") return `<c r="${ref}" s="${style}" t="b"><v>${value ? 1 : 0}</v></c>`;
      return `<c r="${ref}" s="${style}" t="inlineStr"><is><t xml:space="preserve">${escapeXml(value == null ? "" : String(value))}</t></is></c>`;
    }).join("");
    return `<row r="${excelRow}" ht="${height}" customHeight="1" spans="1:${columnCount}">${cells}</row>`;
  }).join("");
  const columns = Array.from({ length: columnCount }, (_, index) => {
    const fallback = index === 0 ? 24 : 16;
    const width = Math.min(80, Math.max(8, sheet.widths?.[index] ?? fallback));
    return `<col min="${index + 1}" max="${index + 1}" width="${width}" customWidth="1"/>`;
  }).join("");
  const xSplit = Math.max(0, Math.min(sheet.freezeColumns ?? 0, columnCount));
  const activeCell = `${columnName(xSplit + 1)}${headerRow + 1}`;
  const pane = `<pane${xSplit ? ` xSplit="${xSplit}"` : ""} ySplit="${headerRow}" topLeftCell="${activeCell}" activePane="${xSplit ? "bottomRight" : "bottomLeft"}" state="frozen"/>`;
  const tabColor = sheet.tabColor ? `<tabColor rgb="${normalizeColor(sheet.tabColor, "FF0F8C83")}"/>` : "";
  const filter = sheet.autoFilter === false ? "" : `<autoFilter ref="A${headerRow}:${lastColumn}${lastRow}"/>`;
  const merges = firstDataRow === 4 ? `<mergeCells count="2"><mergeCell ref="A1:${lastColumn}1"/><mergeCell ref="A2:${lastColumn}2"/></mergeCells>` : "";
  let priority = 1;
  const colorScales = (sheet.colorScaleColumns ?? []).map((column) => {
    const sqref = detailRanges(sheet, column, firstDataRow);
    if (!sqref) return "";
    const [negative, neutral, positive] = sheet.colorScaleColors ?? ["FFF4CCCC", "FFFFFFFF", "FFD9EAD3"];
    const [minimum, midpoint, maximum] = sheet.colorScaleThresholds ?? [-1, 0, 1];
    const bounds = `<cfvo type="num" val="${minimum}"/><cfvo type="num" val="${midpoint}"/><cfvo type="num" val="${maximum}"/>`;
    return `<conditionalFormatting sqref="${sqref}"><cfRule type="colorScale" priority="${priority++}"><colorScale>${bounds}<color rgb="${normalizeColor(negative, "FFF4CCCC")}"/><color rgb="${normalizeColor(neutral, "FFFFFFFF")}"/><color rgb="${normalizeColor(positive, "FFD9EAD3")}"/></colorScale></cfRule></conditionalFormatting>`;
  }).join("");
  const warnings = (sheet.warningRules ?? []).map((rule) => {
    const sqref = detailRanges(sheet, rule.column, firstDataRow);
    if (!sqref) return "";
    const threshold = sheet.percentagePoints?.includes(rule.column) ? rule.threshold / 100 : rule.threshold;
    return `<conditionalFormatting sqref="${sqref}"><cfRule type="cellIs" dxfId="0" priority="${priority++}" operator="greaterThanOrEqual"><formula>${threshold}</formula></cfRule></conditionalFormatting>`;
  }).join("");
  const progressComparisons = (sheet.progressComparisons ?? []).flatMap((rule) =>
    sheet.rows.flatMap((_, rowIndex) => {
      const kind = rowKind(sheet, rowIndex);
      if (kind !== "detail" && !(kind === "total" && rule.includeTotal)) return [];
      const expectation = progressExpectation(rule, rowIndex);
      if (!expectation) return [];
      const ref = `${columnName(rule.column)}${firstDataRow + rowIndex}`;
      const expected = sheet.percentagePoints?.includes(rule.column) ? expectation.expected / 100 : expectation.expected;
      return [`<conditionalFormatting sqref="${ref}"><cfRule type="cellIs" dxfId="1" priority="${priority++}" operator="greaterThanOrEqual"><formula>${expected}</formula></cfRule><cfRule type="cellIs" dxfId="2" priority="${priority++}" operator="lessThan"><formula>${expected}</formula></cfRule></conditionalFormatting>`];
    }),
  ).join("");
  const orientation = sheet.landscape === false || columnCount <= 7 ? "portrait" : "landscape";
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetPr>${tabColor}<pageSetUpPr fitToPage="1"/></sheetPr><dimension ref="A1:${lastColumn}${lastRow}"/><sheetViews><sheetView workbookViewId="0" showGridLines="0" zoomScale="90" zoomScaleNormal="90">${pane}<selection pane="${xSplit ? "bottomRight" : "bottomLeft"}" activeCell="${activeCell}" sqref="${activeCell}"/></sheetView></sheetViews><sheetFormatPr defaultRowHeight="20"/><cols>${columns}</cols><sheetData>${introRows}${body}</sheetData>${filter}${merges}${colorScales}${warnings}${progressComparisons}<printOptions gridLines="0" horizontalCentered="0"/><pageMargins left="0.25" right="0.25" top="0.45" bottom="0.45" header="0.2" footer="0.2"/><pageSetup orientation="${orientation}" paperSize="9" fitToWidth="1" fitToHeight="0"/><headerFooter><oddFooter>&amp;L&amp;BSRR360&amp;B&amp;C&amp;D&amp;RPage &amp;P / &amp;N</oddFooter></headerFooter></worksheet>`;
}

function baseStylesXml() {
  const xf = (style: StyleDefinition) => {
    const alignment = `<alignment horizontal="${style.horizontal}" vertical="center"${style.wrap ? ` wrapText="1"` : ""}/>`;
    return `<xf numFmtId="${style.numFmtId}" fontId="${style.fontId}" fillId="${style.fillId}" borderId="${style.borderId}" xfId="0" applyAlignment="1"${style.fontId ? ` applyFont="1"` : ""}${style.fillId ? ` applyFill="1"` : ""}${style.borderId ? ` applyBorder="1"` : ""}${style.numFmtId ? ` applyNumberFormat="1"` : ""}>${alignment}</xf>`;
  };
  const fills = [
    `<fill><patternFill patternType="none"/></fill>`,
    `<fill><patternFill patternType="gray125"/></fill>`,
    `<fill><patternFill patternType="solid"><fgColor rgb="FF153F55"/><bgColor indexed="64"/></patternFill></fill>`,
    `<fill><patternFill patternType="solid"><fgColor rgb="FFF4B183"/><bgColor indexed="64"/></patternFill></fill>`,
    `<fill><patternFill patternType="solid"><fgColor rgb="FFD9E2F3"/><bgColor indexed="64"/></patternFill></fill>`,
    `<fill><patternFill patternType="solid"><fgColor rgb="FFB4C6E7"/><bgColor indexed="64"/></patternFill></fill>`,
    `<fill><patternFill patternType="solid"><fgColor rgb="FFDDEBF7"/><bgColor indexed="64"/></patternFill></fill>`,
    `<fill><patternFill patternType="solid"><fgColor rgb="FFFFF2CC"/><bgColor indexed="64"/></patternFill></fill>`,
    `<fill><patternFill patternType="solid"><fgColor rgb="FFF2F7F8"/><bgColor indexed="64"/></patternFill></fill>`,
  ];
  const borders = [
    `<border><left/><right/><top/><bottom/><diagonal/></border>`,
    `<border><left/><right/><top/><bottom style="thin"><color rgb="FFD9E3E8"/></bottom><diagonal/></border>`,
    `<border><left/><right style="thin"><color rgb="FF6E8A99"/></right><top/><bottom style="medium"><color rgb="FF0F8C83"/></bottom><diagonal/></border>`,
    `<border><left/><right style="thin"><color rgb="FFFCE4D6"/></right><top/><bottom style="medium"><color rgb="FFC55A11"/></bottom><diagonal/></border>`,
    `<border><left/><right/><top style="thin"><color rgb="FF8EA9B7"/></top><bottom style="thin"><color rgb="FF8EA9B7"/></bottom><diagonal/></border>`,
    `<border><left/><right/><top style="medium"><color rgb="FF0F8C83"/></top><bottom style="double"><color rgb="FF0F8C83"/></bottom><diagonal/></border>`,
  ];
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><numFmts count="7"><numFmt numFmtId="164" formatCode="#,##0"/><numFmt numFmtId="165" formatCode="#,##0.00"/><numFmt numFmtId="166" formatCode="0.0%"/><numFmt numFmtId="167" formatCode="0.0%"/><numFmt numFmtId="168" formatCode="dd.mm.yyyy"/><numFmt numFmtId="169" formatCode="dd.mm.yyyy hh:mm"/><numFmt numFmtId="170" formatCode="[h]:mm:ss"/></numFmts><fonts count="6"><font><sz val="10"/><name val="Aptos"/><family val="2"/><color rgb="FF173042"/></font><font><b/><color rgb="FFFFFFFF"/><sz val="10"/><name val="Aptos"/><family val="2"/></font><font><b/><color rgb="FF173042"/><sz val="10"/><name val="Aptos"/><family val="2"/></font><font><b/><color rgb="FF12384D"/><sz val="14"/><name val="Aptos Display"/><family val="2"/></font><font><i/><color rgb="FF607D8B"/><sz val="9"/><name val="Aptos"/><family val="2"/></font><font><b/><color rgb="FF173042"/><sz val="10"/><name val="Aptos"/><family val="2"/></font></fonts><fills count="${fills.length}">${fills.join("")}</fills><borders count="${borders.length}">${borders.join("")}</borders><cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs><cellXfs count="${STYLE_DEFINITIONS.length}">${STYLE_DEFINITIONS.map(xf).join("")}</cellXfs><cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles><dxfs count="1"><dxf><font><b/><color rgb="FF9C0006"/></font><fill><patternFill patternType="solid"><fgColor rgb="FFFFC7CE"/><bgColor indexed="64"/></patternFill></fill></dxf></dxfs><tableStyles count="0" defaultTableStyle="TableStyleMedium2" defaultPivotStyle="PivotStyleLight16"/></styleSheet>`;
}

function buildStylePlan(sheets: WorkbookSheet[]): WorkbookStylePlan {
  const labels = Array.from(new Set(sheets.flatMap((sheet) => (sheet.progressComparisons ?? []).flatMap((item) => [item.expectedLabel, ...(item.rowExpectations ?? []).flatMap((expectation) => expectation ? [expectation.expectedLabel] : [])]))));
  const comparisonStyleIds = new Map<string, number>();
  const comparisonFormats = labels.map((label, index) => {
    const firstStyleId = STYLE_DEFINITIONS.length + index * 3;
    comparisonStyleIds.set(`${label}|detail`, firstStyleId);
    comparisonStyleIds.set(`${label}|total-blue`, firstStyleId + 1);
    comparisonStyleIds.set(`${label}|total-yellow`, firstStyleId + 2);
    return {
      numFmtId: 171 + index,
      formatCode: `0.0% " | ${label}"`,
      styles: [
        { numFmtId: 171 + index, fontId: 0, fillId: 0, borderId: 1, horizontal: "right" },
        { numFmtId: 171 + index, fontId: 5, fillId: 6, borderId: 5, horizontal: "right" },
        { numFmtId: 171 + index, fontId: 5, fillId: 7, borderId: 5, horizontal: "right" },
      ] as StyleDefinition[],
    };
  });
  return { comparisonStyleIds, comparisonFormats };
}

function stylesXml(stylePlan: WorkbookStylePlan) {
  const base = baseStylesXml();
  const xf = (style: StyleDefinition) => {
    const alignment = `<alignment horizontal="${style.horizontal}" vertical="center"${style.wrap ? ` wrapText="1"` : ""}/>`;
    return `<xf numFmtId="${style.numFmtId}" fontId="${style.fontId}" fillId="${style.fillId}" borderId="${style.borderId}" xfId="0" applyAlignment="1"${style.fontId ? ` applyFont="1"` : ""}${style.fillId ? ` applyFill="1"` : ""}${style.borderId ? ` applyBorder="1"` : ""}${style.numFmtId ? ` applyNumberFormat="1"` : ""}>${alignment}</xf>`;
  };
  const customNumFmts = stylePlan.comparisonFormats.map((item) => `<numFmt numFmtId="${item.numFmtId}" formatCode="${escapeXml(item.formatCode)}"/>`).join("");
  const customCellXfs = stylePlan.comparisonFormats.flatMap((item) => item.styles.map(xf)).join("");
  const extraDxfs = `<dxf><font><b/><color rgb="FF00854A"/></font></dxf><dxf><font><b/><color rgb="FFD61F2C"/></font></dxf>`;
  return base
    .replace(`<numFmts count="7">`, `<numFmts count="${7 + stylePlan.comparisonFormats.length}">`)
    .replace(`</numFmts>`, `${customNumFmts}</numFmts>`)
    .replace(`<cellXfs count="${STYLE_DEFINITIONS.length}">`, `<cellXfs count="${STYLE_DEFINITIONS.length + stylePlan.comparisonFormats.length * 3}">`)
    .replace(`</cellXfs>`, `${customCellXfs}</cellXfs>`)
    .replace(`<dxfs count="1">`, `<dxfs count="3">`)
    .replace(`</dxfs>`, `${extraDxfs}</dxfs>`);
}

function workbookXml(sheets: WorkbookSheet[]) {
  const definedNames = sheets.flatMap((sheet, index) => {
    const { headerRow, lastRow, lastColumn } = sheetLayout(sheet);
    const quotedName = `'${sheet.name.replace(/'/g, "''")}'`;
    const titles = sheet.autoFilter === false ? [] : [`<definedName name="_xlnm.Print_Titles" localSheetId="${index}">${quotedName}!$${headerRow}:$${headerRow}</definedName>`];
    return [...titles, `<definedName name="_xlnm.Print_Area" localSheetId="${index}">${quotedName}!$A$1:$${lastColumn}$${lastRow}</definedName>`];
  }).join("");
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><fileVersion appName="xl" lastEdited="7" lowestEdited="7" rupBuild="28329"/><workbookPr defaultThemeVersion="164011"/><bookViews><workbookView xWindow="0" yWindow="0" windowWidth="24000" windowHeight="15000" activeTab="0"/></bookViews><sheets>${sheets.map((sheet, index) => `<sheet name="${escapeXml(sheet.name)}" sheetId="${index + 1}" r:id="rId${index + 1}"/>`).join("")}</sheets><definedNames>${definedNames}</definedNames><calcPr calcId="191029" fullCalcOnLoad="1"/></workbook>`;
}

export async function createWorkbookBytes(inputSheets: WorkbookSheet[], properties: WorkbookProperties = {}) {
  const { strToU8, zipSync } = await import("fflate");
  validateWorkbookSheets(inputSheets);
  const sheets = normalizeSheets(inputSheets);
  const stylePlan = buildStylePlan(sheets);
  const files: Record<string, Uint8Array> = {};
  const put = (path: string, content: string) => { files[path] = strToU8(content); };
  const created = new Date().toISOString();
  put("[Content_Types].xml", `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/><Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/><Override PartName="/docProps/app.xml" ContentType="application/vnd.openxmlformats-officedocument.extended-properties+xml"/>${sheets.map((_, index) => `<Override PartName="/xl/worksheets/sheet${index + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join("")}</Types>`);
  put("_rels/.rels", `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties" Target="docProps/core.xml"/><Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/extended-properties" Target="docProps/app.xml"/></Relationships>`);
  put("docProps/core.xml", `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><cp:coreProperties xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:dcterms="http://purl.org/dc/terms/" xmlns:dcmitype="http://purl.org/dc/dcmitype/" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"><dc:title>${escapeXml(properties.title ?? "SRR360 Raporu")}</dc:title><dc:subject>${escapeXml(properties.subject ?? "SRR360 profesyonel Excel raporu")}</dc:subject><dc:creator>${escapeXml(properties.creator ?? "SRR360")}</dc:creator><cp:lastModifiedBy>SRR360</cp:lastModifiedBy><dcterms:created xsi:type="dcterms:W3CDTF">${created}</dcterms:created><dcterms:modified xsi:type="dcterms:W3CDTF">${created}</dcterms:modified></cp:coreProperties>`);
  put("docProps/app.xml", `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/extended-properties" xmlns:vt="http://schemas.openxmlformats.org/officeDocument/2006/docPropsVTypes"><Application>SRR360</Application><AppVersion>1.0</AppVersion><Company>Silk Road Retail</Company></Properties>`);
  put("xl/workbook.xml", workbookXml(sheets));
  put("xl/_rels/workbook.xml.rels", `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${sheets.map((_, index) => `<Relationship Id="rId${index + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${index + 1}.xml"/>`).join("")}<Relationship Id="rId${sheets.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>`);
  put("xl/styles.xml", stylesXml(stylePlan));
  sheets.forEach((sheet, index) => put(`xl/worksheets/sheet${index + 1}.xml`, sheetXml(sheet, stylePlan)));
  return zipSync(files, { level: 6 });
}

export async function downloadWorkbook(sheets: WorkbookSheet[], filename: string, properties: WorkbookProperties = {}) {
  const bytes = await createWorkbookBytes(sheets, properties);
  const output = properties.reportKind
    ? (await import("./reportExcel")).formatReportWorkbook(bytes, normalizeSheets(sheets), properties.reportKind)
    : bytes;
  const blob = new Blob([normalizeWorkbookMetricHeadings(output, filename.match(/_(tr|en|ru|uz)\.xlsx$/)?.[1] ?? "tr", filename.startsWith("urun_analizi_"))], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
}
